import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { 
  FiFilter, 
  FiSearch, 
  FiMapPin, 
  FiX, 
  FiChevronDown,
  FiAlertTriangle
} from "react-icons/fi";
import api from "../services/api";
import SearchResultCard from "../components/SearchResultCard";
import {
  filterProductsByCity,
  getNigerCityByKey,
  getUserPreferredCityKey,
  NIGER_CITIES,
  sortProductsByNearestCity,
} from "../utils/nigerLocations";
import Loader from "../components/Loader";

const SearchResultsPage = () => {
  const { t } = useTranslation();
  const { searchTerm, categoryId, categoryName } = useParams();
  const [searchParams] = useSearchParams();

  const querySearch =
    searchParams.get("search") ||
    searchParams.get("q") ||
    searchParams.get("query") ||
    "";
  const queryCategory = searchParams.get("category") || "";

  const activeSearchTerm = searchTerm || querySearch;
  const activeCategoryId = categoryId || queryCategory;

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Filter States
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [minRating, setMinRating] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [userCityKey, setUserCityKey] = useState("");

  useEffect(() => {
    const syncUserCity = () => setUserCityKey(getUserPreferredCityKey());
    syncUserCity();
    window.addEventListener("chafadi-addresses-updated", syncUserCity);
    return () =>
      window.removeEventListener("chafadi-addresses-updated", syncUserCity);
  }, []);

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      setError("");

      const params = new URLSearchParams();
      if (activeSearchTerm) params.append("search", activeSearchTerm);
      if (activeCategoryId) params.append("category", activeCategoryId);
      if (minPrice) params.append("min_price", minPrice);
      if (maxPrice) params.append("max_price", maxPrice);
      if (verifiedOnly) params.append("verified_only", "true");
      if (minRating) params.append("min_rating", minRating);

      try {
        const response = await api.get(`/products/?${params.toString()}`);
        const fetchedProducts = response.data.results || response.data;
        const filteredProducts = filterProductsByCity(fetchedProducts, selectedLocation);
        const sortedProducts = sortProductsByNearestCity(
          filteredProducts,
          selectedLocation || userCityKey
        );
        setProducts(sortedProducts);
      } catch (err) {
        setError(t("searchResults.syncFailure"));
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [activeSearchTerm, activeCategoryId, minPrice, maxPrice, verifiedOnly, minRating, selectedLocation, userCityKey, t]);

  const handleReset = () => {
    setMinPrice("");
    setMaxPrice("");
    setVerifiedOnly(false);
    setMinRating("");
    setSelectedLocation("");
  };

  const pageTitle = activeSearchTerm
    ? t("searchResults.title", { searchTerm: activeSearchTerm })
    : categoryName
    ? t("searchResults.productsInCategory", { categoryName })
    : t("searchResults.allProducts");
  const activeCity = getNigerCityByKey(selectedLocation || userCityKey);
  const introText = activeSearchTerm
    ? `Explore Chafadi results for ${activeSearchTerm} with trusted sellers, localized delivery options, and marketplace listings tailored for shoppers in Niger.`
    : categoryName
    ? `Browse ${categoryName} products on Chafadi with verified sellers, pricing built for Niger, and fast local marketplace discovery.`
    : "Discover products across Chafadi's marketplace in Niger, with verified sellers, localized delivery, and category-based shopping.";

  useEffect(() => {
    document.title = `${pageTitle} | Chafadi`;
    const descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) {
      descriptionTag.setAttribute("content", introText);
    }
    const canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag) {
      canonicalTag.setAttribute("href", window.location.href);
    }
  }, [introText, pageTitle]);

  const filtersPanel = (
    <div className="bg-[var(--color-bg-secondary)] rounded-[20px] border border-[var(--color-border)] p-6 shadow-[0_2px_24px_rgba(0,0,0,0.05)] sticky top-28 overflow-hidden">
      <div className="flex justify-between items-center mb-6">
        <h5 className="text-[12px] font-semibold text-[var(--color-text-primary)] uppercase tracking-wider flex items-center gap-2">
          <FiFilter className="text-[var(--color-accent)]" />
          {t("searchResults.filter")}
        </h5>
        <button
          onClick={handleReset}
          className="text-[11px] font-medium text-[var(--color-accent)] hover:underline"
        >
          {t("searchResults.reset")}
        </button>
      </div>

      <div className="space-y-6">
        {/* Price Range */}
        <div className="space-y-2.5">
          <label className="block text-[11px] font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
            {t("searchResults.priceRange")}
          </label>
          <div className="grid grid-cols-2 gap-2.5">
            <input
              type="number"
              placeholder={t("searchResults.min")}
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] focus:border-[var(--color-accent)] rounded-full px-3.5 py-2 text-xs text-[var(--color-text-primary)] outline-none transition-all placeholder:text-[var(--color-text-tertiary)]"
            />
            <input
              type="number"
              placeholder={t("searchResults.max")}
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] focus:border-[var(--color-accent)] rounded-full px-3.5 py-2 text-xs text-[var(--color-text-primary)] outline-none transition-all placeholder:text-[var(--color-text-tertiary)]"
            />
          </div>
        </div>

        {/* Verified Only */}
        <div className="space-y-2">
          <label className="block text-[11px] font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
            {t("searchResults.verifiedOnly")}
          </label>
          <label className="relative inline-flex items-center cursor-pointer group">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={verifiedOnly}
              onChange={(e) => setVerifiedOnly(e.target.checked)}
            />
            <div className="w-11 h-6 bg-[var(--color-border)] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-5 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--color-accent)]"></div>
            <span className="ml-3 text-[12px] font-medium text-[var(--color-text-primary)]">
              {t("searchResults.verifiedSellersOnly")}
            </span>
          </label>
        </div>

        {/* Rating */}
        <div className="space-y-2">
          <label className="block text-[11px] font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
            {t("searchResults.minRating")}
          </label>
          <div className="relative">
            <select
              value={minRating}
              onChange={(e) => setMinRating(e.target.value)}
              className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] focus:border-[var(--color-accent)] rounded-full px-4 py-2 text-xs text-[var(--color-text-primary)] outline-none appearance-none cursor-pointer transition-all"
            >
              <option value="">{t("searchResults.anyFeedbackScore")}</option>
              <option value="4.5">4.5+ {t("searchResults.prestige")}</option>
              <option value="4">4.0+ {t("searchResults.highGrade")}</option>
              <option value="3">3.0+ {t("searchResults.standard")}</option>
            </select>
            <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)] pointer-events-none" />
          </div>
        </div>

        {/* Location */}
        <div className="space-y-2">
          <label className="block text-[11px] font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider">
            {t("searchResults.location")}
          </label>
          <div className="relative">
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="w-full bg-[var(--color-bg)] border border-[var(--color-border)] focus:border-[var(--color-accent)] rounded-full px-4 py-2 text-xs text-[var(--color-text-primary)] outline-none appearance-none cursor-pointer transition-all"
            >
              <option value="">{t("searchResults.nearestToMe")}</option>
              {NIGER_CITIES.map((city) => (
                <option key={city.key} value={city.key}>
                  {city.name}
                </option>
              ))}
            </select>
            <FiMapPin className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)] pointer-events-none" />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-[var(--color-bg)] text-[var(--color-text-primary)] min-h-screen font-sans">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        
        {/* Mobile Filter Trigger */}
        <div className="lg:hidden mb-6">
          <button
            onClick={() => setShowMobileFilters(true)}
            className="btn-secondary w-full h-11 text-[13px] gap-2 rounded-full shadow-sm"
          >
            <FiFilter />
            {t("searchResults.showFilters")}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Filters Sidebar */}
          <aside className="hidden lg:block lg:col-span-3">
            {filtersPanel}
          </aside>

          {/* Results Area */}
          <div className="lg:col-span-9 space-y-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-[var(--color-border)]">
              <div className="space-y-2">
                <h1 className="text-3xl sm:text-4xl font-semibold tracking-[-0.03em] text-[var(--color-text-primary)]">
                  {pageTitle}
                </h1>
                <p className="max-w-2xl text-xs sm:text-sm text-[var(--color-text-secondary)] leading-relaxed">
                  {introText}
                </p>
                <div className="flex flex-wrap items-center gap-2.5 pt-1">
                  <div className="px-3 py-1 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-full text-[11px] font-medium text-[var(--color-text-secondary)]">
                    {t("searchResults.productsFound", { count: products.length })}
                  </div>
                  {activeCity && (
                    <div className="px-3 py-1 bg-[var(--color-accent)]/10 text-[var(--color-accent)] rounded-full text-[11px] font-medium flex items-center gap-1.5">
                      <FiMapPin size={12} />
                      {t("searchResults.localized", { city: activeCity.name })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {loading ? (
              <Loader fullPage={false} />
            ) : error ? (
              <div className="bg-red-500/10 border border-red-500/20 p-8 rounded-[22px] text-center space-y-4 animate-fade-in">
                <div className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center mx-auto">
                  <FiAlertTriangle size={24} />
                </div>
                <h4 className="text-base font-semibold text-[var(--color-text-primary)]">{error}</h4>
                <button
                  onClick={() => window.location.reload()}
                  className="btn-primary h-10 px-6 text-[12px]"
                >
                  {t("searchResults.reinitializeSync")}
                </button>
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6 animate-fade-in">
                {products.map((product) => (
                  <SearchResultCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="bg-[var(--color-bg-secondary)] rounded-[24px] border border-[var(--color-border)] p-12 text-center shadow-sm animate-fade-in">
                <div className="w-20 h-20 bg-[var(--color-bg)] rounded-full flex items-center justify-center mx-auto mb-6 text-[var(--color-text-tertiary)] border border-[var(--color-border)]">
                  <FiSearch size={32} />
                </div>
                <h4 className="text-xl font-semibold text-[var(--color-text-primary)] mb-2">
                  {t("searchResults.zeroMatches")}
                </h4>
                <p className="text-xs sm:text-sm text-[var(--color-text-secondary)] max-w-xs mx-auto leading-relaxed">
                  {t("searchResults.noMatchesDesc")}
                </p>
                <button 
                  onClick={handleReset}
                  className="btn-primary mt-6 h-11 px-8 text-[13px]"
                >
                  {t("searchResults.clearAllFilters")}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Bottom Sheet (DESIGN.md §7) */}
        {showMobileFilters && (
          <div className="fixed inset-0 z-[1000] lg:hidden">
            <div 
              className="absolute inset-0 bg-black/50 backdrop-blur-md animate-fade-in"
              onClick={() => setShowMobileFilters(false)}
            />
            <div className="absolute inset-x-0 bottom-0 bg-[var(--color-bg)] rounded-t-[32px] p-6 shadow-[0_-20px_60px_rgba(0,0,0,0.22)] animate-slide-up max-h-[88vh] overflow-y-auto no-scrollbar border-t border-[var(--color-border)] flex flex-col">
              {/* Bottom Sheet Handle */}
              <div className="flex justify-center mb-4 cursor-pointer" onClick={() => setShowMobileFilters(false)}>
                <div className="w-12 h-1.5 bg-[var(--color-border)] hover:bg-[var(--color-text-tertiary)] rounded-full transition-colors" />
              </div>

              <div className="flex items-center justify-between mb-5 shrink-0">
                <div>
                  <h3 className="text-lg font-semibold text-[var(--color-text-primary)] tracking-tight">
                    {t("searchResults.filter", "Filter Products")}
                  </h3>
                  <p className="text-[11px] text-[var(--color-text-secondary)]">
                    {t("searchResults.narrowResults", "Refine specifications & categories")}
                  </p>
                </div>
                <button 
                  onClick={() => setShowMobileFilters(false)}
                  className="w-8 h-8 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] flex items-center justify-center text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] active:scale-95 transition-all"
                  aria-label="Close filters"
                >
                  <FiX size={16} />
                </button>
              </div>
              
              <div className="flex-1 min-h-0 pb-6">
                {filtersPanel}
              </div>

              <div className="sticky bottom-0 bg-[var(--color-bg)]/95 backdrop-blur-md pt-3 pb-[max(env(safe-area-inset-bottom),16px)] border-t border-[var(--color-border)] shrink-0">
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="btn-primary w-full h-12 text-sm font-semibold rounded-full shadow-sm"
                >
                  {t("searchResults.applyParameters", "Apply Filters")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResultsPage;
